/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Button, CircularProgress } from '@mui/material';
import { Warning } from '@mui/icons-material';
import isNil from 'lodash/isNil';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsRequestingTerminalHistory, selectRequestTerminalHistoryError } from '../../../../../../store';
import { chartContent } from '../../../../../fleet/FullTailView/charts/ChartItems';
import ChartsList from '../../../../../fleet/FullTailView/charts/ChartsList';
import ChartsContainerWrapper from '../../../../../fleet/FullTailView/charts/ChartsContainerWrapper';
import ChartsSeriesColorWrapper from '../../../../../fleet/FullTailView/charts/ChartsSeriesColorWrapper';
import AllProtocolsModal from '../../../../../fleet/FullTailView/Protocols/AllProtocolsModal';
import { CHART_TABS_HEIGHT_PX } from '../ChartTabs';

interface PerformanceChartsContainerProps {
  canShowCharts: boolean;
  isRteTabSelected: boolean;
  onRetry: () => void;
}

const PerformanceChartsContainer = ({ canShowCharts, isRteTabSelected, onRetry }: PerformanceChartsContainerProps) => {
  const [chartList, setChartList] = useState({ ...chartContent });
  const isRequestingTerminalHistory = useSelector(selectIsRequestingTerminalHistory);
  const requestTerminalHistoryError = useSelector(selectRequestTerminalHistoryError);

  // RTE has no charts yet, so it shows an empty charts container and ignores the terminal history request
  const shouldShowLoadingOrError =
    !canShowCharts || (!isRteTabSelected && (isRequestingTerminalHistory || !isNil(requestTerminalHistoryError)));

  return (
    <ChartsContainerWrapper printMode={false} mapHeight={CHART_TABS_HEIGHT_PX} leftMargin={0}>
      <ChartsSeriesColorWrapper>
        {shouldShowLoadingOrError ? (
          <div className="ftv__charts__loading--container">
            {!isNil(requestTerminalHistoryError) ? (
              <div className="error__container">
                <div className="error__message__container">
                  <Warning />
                  <div>Error Fetching Chart Data</div>
                  <Button color="primary" variant="text" onClick={onRetry}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <CircularProgress className="progress ftv__charts__loading" />
            )}
          </div>
        ) : (
          <ChartsList chartList={isRteTabSelected ? {} : chartList} onChartListChange={setChartList} />
        )}
      </ChartsSeriesColorWrapper>
      <AllProtocolsModal />
    </ChartsContainerWrapper>
  );
};

export default PerformanceChartsContainer;
